/**
 * The six service lines, equally weighted.
 *
 * Copy leads on capability and specifics — what gets built, with what tools,
 * and what you receive — rather than on track record. That is the honest and
 * more persuasive position for an early-stage firm, and it avoids any claim
 * about client volume or years in business that cannot be substantiated.
 *
 * `name` is deliberately SHORT: it renders as the services-page H2 but also as
 * a row in the footer and the home grid, where a long label wraps badly. The
 * search terms therefore live in `headline` and `summary`, which sit directly
 * beside the heading and have room for them.
 */

export interface Service {
  slug: string;
  /** Short label for nav and cards. */
  name: string;
  /** Card headline — the promise, in plain words. */
  headline: string;
  /** One-sentence summary for the Home grid. */
  summary: string;
  /** Longer positioning for the Services page section. */
  body: string;
  /** Concrete things the client receives. */
  deliverables: string[];
  /** Tools and platforms, shown as chips. */
  stack: string[];
}

export const services: Service[] = [
  {
    slug: 'software-development',
    name: 'Software & Web Development',
    headline: 'Custom web application development, built to be maintained',
    summary:
      'Custom web application development for platforms, internal tools and customer-facing products, architected so the second year costs less than the first.',
    body: 'We build web applications end to end: interface, API, database, deployment. The work is scoped so you own something maintainable at the end of it: typed code, documented decisions, and infrastructure you can hand to another team without a rewrite. Most engagements start with a short discovery phase, because the cheapest bug is the one caught before anyone writes code.',
    deliverables: [
      'Discovery and technical scoping document',
      'Production application with typed, tested code',
      'API documentation and deployment pipeline',
      'Handover session and maintenance runbook',
    ],
    stack: ['React', 'Next.js', 'TypeScript', 'Node.js', 'PostgreSQL', 'Docker', 'Twilio'],
  },
  {
    slug: 'mobile-development',
    name: 'Mobile App Development',
    headline: 'Mobile app development for iOS and Android, one codebase',
    summary:
      'Cross-platform mobile app development in React Native: iOS and Android apps that feel native, with offline handling and app store submission included.',
    body: 'Cross-platform development means you fund one team, not two, and features land on both platforms in the same sprint. We handle the parts that usually get discovered late: offline state, push notifications, deep links, biometric auth, and the store review process itself. Where a screen genuinely needs native performance, we drop to native for that screen rather than the whole app.',
    deliverables: [
      'iOS and Android builds from a single codebase',
      'Offline-capable data layer and sync strategy',
      'App Store and Play Store submission',
      'Crash reporting and release pipeline',
    ],
    stack: ['React Native', 'Expo', 'TypeScript', 'Firebase', 'Fastlane'],
  },
  {
    slug: 'ai-data',
    name: 'AI & Data Solutions',
    headline: 'AI integration services that answer from your data',
    summary:
      'AI integration and retrieval systems grounded in your own documents and records, returning answers with citations you can open and check rather than guesses.',
    body: 'Most useful AI work is retrieval, not training. We connect a language model to your actual documents, tickets and records so answers cite a source you can open and check. That means an evaluation set before launch, so you can measure whether it is right rather than assume it. Where a task is better served by classical methods than an LLM, we say so, because it is usually cheaper and more accurate.',
    deliverables: [
      'Document ingestion and vector search pipeline',
      'Grounded question-answering with source citations',
      'Evaluation set and accuracy benchmarks',
      'Cost and latency budget per query',
    ],
    stack: ['OpenAI', 'Anthropic', 'Pinecone', 'LangChain', 'Python', 'pgvector'],
  },
  {
    slug: 'ai-automation',
    name: 'AI Automation',
    headline: 'AI automation for the repetitive judgement calls',
    summary:
      'Business process automation for triage, classification, extraction and drafting: the recurring decisions that quietly consume a working week.',
    body: 'Some work is too nuanced for a rule and too repetitive for a person: sorting inbound email, extracting fields from invoices, drafting first-pass replies, flagging what needs a human. We build these as narrow, observable systems with a confidence threshold and a human review path, so nothing important is decided silently. You see what it did and why, and you can turn any step off.',
    deliverables: [
      'Automated classification or extraction pipeline',
      'Confidence thresholds with human-in-the-loop review',
      'Audit log of every automated decision',
      'Accuracy monitoring and alerting',
    ],
    stack: ['OpenAI', 'Anthropic', 'Python', 'n8n', 'Webhooks'],
  },
  {
    slug: 'qa-automation',
    name: 'QA Automation',
    headline: 'QA automation services that catch it before customers do',
    summary:
      'Automated software testing with Playwright: end-to-end suites that run on every commit and tell you exactly what broke, with a video of it breaking.',
    body: 'A test suite earns its keep when it fails usefully. We write end-to-end tests against real user journeys (signup, checkout, the flows that cost money when they break) and wire them into CI so nothing merges past a red build. Failures come with a trace and a video, so a developer sees what happened instead of trying to reproduce it. We also cover the cases teams usually skip: mobile viewports, slow networks, and keyboard-only navigation.',
    deliverables: [
      'End-to-end suite covering critical user journeys',
      'CI integration with per-commit runs',
      'Video and trace capture on every failure',
      'Cross-browser and mobile viewport coverage',
    ],
    stack: ['Playwright', 'Cypress', 'GitHub Actions', 'TypeScript'],
  },
  {
    slug: 'workflow-automation',
    name: 'Workflow Automation',
    headline: 'Workflow automation across the tools you already pay for',
    summary:
      'n8n and Make.com integrations that move data between your existing systems automatically, so nobody is retyping it by hand.',
    body: 'Most operational drag is not a missing tool. It is two tools that do not talk, bridged by somebody copying fields between them. We map the actual process first, then automate the handoffs: CRM to invoicing, form to database, alert to the right channel. Built with error handling and retries, because an automation that fails silently is worse than no automation. Where it makes sense we build on n8n so you can self-host and own the workflows outright.',
    deliverables: [
      'Process map of the current manual workflow',
      'Automated pipelines with retry and error alerting',
      'Self-hosted or cloud deployment, your choice',
      'Documentation so your team can edit them',
    ],
    stack: ['n8n', 'Make.com', 'Zapier', 'Twilio', 'REST APIs', 'Webhooks'],
  },
];
